import React, { useState, useEffect } from 'react';
import { cmsApi } from '../../../lib/api/cms';
import { translationsApi } from '../../../lib/api/translations';
import ImageUpload from '../ImageUpload';

interface TeamMember {
  id: string;
  name: string;
  position: string;
  phone: string;
  email: string;
}

interface AboutContent {
  subtitle: string;
  yearsExperience: string;
  founded: string;
  address: string;
  phone1: string;
  phone2: string;
  email: string;
}

const AboutEditor: React.FC = () => {
  const [aboutData, setAboutData] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [aboutContent, setAboutContent] = useState<AboutContent>({
    subtitle: '',
    yearsExperience: '',
    founded: '',
    address: '',
    phone1: '',
    phone2: '',
    email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default values from the website
  const defaultContent = {
    subtitle: 'Forestry OÜ on tegutsenud Eesti metsandussektoris aastast 2005, pakkudes klientidele kvaliteetseid teenuseid ja usaldusväärseid lahendusi kogu riigi territooriumil.',
    yearsExperience: 'Usaldusväärne partner Eesti metsandusvaldkonnas juba 20 aastat',
    founded: '2005',
    address: 'Osmussaare tänav 8, 13811 Tallinn, Eesti',
    phone1: '+372 6707 110',
    phone2: '+372 5663 6076',
    email: 'info@forestryest.ee'
  };

  const defaultTeamMembers = [
    {
      name: 'Ain Meister',
      position: 'Juhatuse liige',
      phone: '5663 6076',
      email: 'ain@forestryest.ee'
    },
    {
      name: 'Risto Esko',
      position: 'Juhatuse liige',
      phone: '5663 8551',
      email: 'risto@forestryest.ee'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [aboutRes, teamRes] = await Promise.all([
        cmsApi.getSection('about'),
        cmsApi.getTeamMembers(),
        loadAboutTranslations()
      ]);

      if (aboutRes.error && aboutRes.error.code !== 'PGRST116') {
        setError('Andmete laadimine ebaõnnestus');
        return;
      }

      setAboutData(aboutRes.data || {});
      
      // If no team members, initialize with defaults
      if (!teamRes.data || teamRes.data.length === 0) {
        await initializeDefaultTeamMembers();
      } else {
        setTeamMembers(teamRes.data || []);
      }
    } catch (err) {
      setError('Andmete laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  const loadAboutTranslations = async () => {
    try {
      const keys = [
        'about.subtitle',
        'about.yearsExperience',
        'about.companyInfo.founded',
        'about.companyInfo.address',
        'about.companyInfo.phone1',
        'about.companyInfo.phone2',
        'about.companyInfo.email'
      ];

      const translations: Record<string, string> = {};
      
      for (const key of keys) {
        const { data } = await translationsApi.getTranslation(key);
        const shortKey = key.split('.').pop() || key;
        translations[shortKey] = data?.value || defaultContent[shortKey as keyof typeof defaultContent] || '';
      }

      setAboutContent({
        subtitle: translations.subtitle,
        yearsExperience: translations.yearsExperience,
        founded: translations.founded,
        address: translations.address,
        phone1: translations.phone1,
        phone2: translations.phone2,
        email: translations.email
      });
    } catch (err) {
      console.error('Failed to load about translations:', err);
      setAboutContent(defaultContent);
    }
  };

  const initializeDefaultTeamMembers = async () => {
    try {
      const memberPromises = defaultTeamMembers.map(member => 
        cmsApi.upsertTeamMember(member)
      );

      await Promise.all(memberPromises);
      const { data } = await cmsApi.getTeamMembers();
      setTeamMembers(data || []);
    } catch (err) {
      console.error('Failed to initialize default team members:', err);
    }
  };

  const handleImageChange = async (imageUrl: string | null) => {
    setSaving(true);
    try {
      const { error } = await cmsApi.upsertSection({
        type: 'about',
        background_image: imageUrl,
        ...aboutData
      });

      if (error) {
        setError('Pildi salvestamine ebaõnnestus');
        return;
      }

      setAboutData((prev: any) => ({ ...prev, background_image: imageUrl }));
    } catch (err) {
      setError('Pildi salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleContentSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const translationUpdates = [
        { key: 'about.subtitle', value: aboutContent.subtitle, locale: 'et' },
        { key: 'about.yearsExperience', value: aboutContent.yearsExperience, locale: 'et' },
        { key: 'about.companyInfo.founded', value: aboutContent.founded, locale: 'et' },
        { key: 'about.companyInfo.address', value: aboutContent.address, locale: 'et' },
        { key: 'about.companyInfo.phone1', value: aboutContent.phone1, locale: 'et' },
        { key: 'about.companyInfo.phone2', value: aboutContent.phone2, locale: 'et' },
        { key: 'about.companyInfo.email', value: aboutContent.email, locale: 'et' }
      ];

      const { error } = await translationsApi.upsertTranslations(translationUpdates);

      if (error) {
        setError('Sisu salvestamine ebaõnnestus');
        return;
      }

      // Trigger website update
      window.dispatchEvent(new CustomEvent('translationsUpdated'));
      
      // Show success message briefly
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      successDiv.textContent = 'Sisu edukalt salvestatud!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (err) {
      setError('Sisu salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleTeamMemberUpdate = async (memberId: string, field: string, value: string) => {
    try {
      const member = teamMembers.find(m => m.id === memberId);
      if (!member) return;

      const updatedMember = { ...member, [field]: value };
      
      const { error } = await cmsApi.upsertTeamMember(updatedMember);
      
      if (error) {
        setError('Meeskonnaliikme andmete salvestamine ebaõnnestus');
        return;
      }

      setTeamMembers(prev => prev.map(m => 
        m.id === memberId ? updatedMember : m
      ));

      // Trigger website update
      window.dispatchEvent(new CustomEvent('translationsUpdated'));

    } catch (err) {
      setError('Meeskonnaliikme andmete salvestamine ebaõnnestus');
    }
  };

  const handleContentChange = (field: keyof AboutContent, value: string) => {
    setAboutContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Meist sektsiooni haldus</h1>
        <p className="mt-2 text-gray-600">
          Halda kõiki "Meie ettevõttest" sektsiooni tekste ja sisu. Muudatused kajastuvad kohe veebilehel.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Main Content */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Põhisisu</h3>
            <p className="text-sm text-gray-500 mt-1">
              Halda ettevõtte põhiinfot ja kirjeldusi
            </p>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ettevõtte kirjeldus
              </label>
              <textarea
                value={aboutContent.subtitle}
                onChange={(e) => handleContentChange('subtitle', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Forestry OÜ on tegutsenud Eesti metsandussektoris..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kogemuse tekst (kuvatakse pildi peal)
              </label>
              <input
                type="text"
                value={aboutContent.yearsExperience}
                onChange={(e) => handleContentChange('yearsExperience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Usaldusväärne partner Eesti metsandusvaldkonnas juba 20 aastat"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asutamisaasta
                </label>
                <input
                  type="text"
                  value={aboutContent.founded}
                  onChange={(e) => handleContentChange('founded', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="2005"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posti aadress
                </label>
                <input
                  type="email"
                  value={aboutContent.email}
                  onChange={(e) => handleContentChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="info@forestryest.ee"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aadress
              </label>
              <input
                type="text"
                value={aboutContent.address}
                onChange={(e) => handleContentChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Osmussaare tänav 8, 13811 Tallinn, Eesti"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon 1
                </label>
                <input
                  type="text"
                  value={aboutContent.phone1}
                  onChange={(e) => handleContentChange('phone1', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="+372 6707 110"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon 2
                </label>
                <input
                  type="text"
                  value={aboutContent.phone2}
                  onChange={(e) => handleContentChange('phone2', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="+372 5663 6076"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleContentSave}
                disabled={saving}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvestamine...
                  </>
                ) : (
                  'Salvesta põhisisu'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Company Image */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Ettevõtte pilt</h3>
            <p className="text-sm text-gray-500 mt-1">
              Pilt kuvatakse "Meie ettevõttest" sektsioonis
            </p>
          </div>
          <div className="p-6">
            <ImageUpload
              currentImage={aboutData?.background_image}
              onImageChange={handleImageChange}
              folder="about"
              label="Ettevõtte pilt"
            />
            <p className="text-sm text-gray-500 mt-2">
              Soovitatud suurus: 1200x800px. Maksimaalne failisuurus: 5MB.
            </p>
          </div>
        </div>

        {/* Team Members */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Juhatuse liikmed</h3>
            <p className="text-sm text-gray-500 mt-1">
              Halda juhatuse liikmete kontaktandmeid. Muudatused salvestatakse automaatselt.
            </p>
          </div>
          <div className="p-6">
            {teamMembers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Meeskonnaliikmeid pole veel lisatud.
              </p>
            ) : (
              <div className="space-y-6">
                {teamMembers.map((member) => (
                  <div key={member.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nimi
                        </label>
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => handleTeamMemberUpdate(member.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Nimi"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ametinimetus
                        </label>
                        <input
                          type="text"
                          value={member.position}
                          onChange={(e) => handleTeamMemberUpdate(member.id, 'position', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Juhatuse liige"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Telefon (ainult numbrid)
                        </label>
                        <input
                          type="text"
                          value={member.phone}
                          onChange={(e) => handleTeamMemberUpdate(member.id, 'phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="5663 6076"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Näide: 5663 6076 (kuvatakse kui "Tel: 5663 6076")
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          E-post
                        </label>
                        <input
                          type="email"
                          value={member.email}
                          onChange={(e) => handleTeamMemberUpdate(member.id, 'email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="nimi@forestryest.ee"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutEditor;